import React, { useState } from 'react';
import Box from 'evcharging/components/Box';
import CalculatorChart from 'evcharging/containers/ev-calculator/CalculatorChart';
import Responsible from 'evcharging/components/Responsible';
import CalculatorResults from 'evcharging/containers/ev-calculator/CalculatorResults';
import useInput from 'evcharging/hooks/useInput';
import { max, min, numbersOnly } from 'evcharging/app/validation/filters';
import SliderInput from 'evcharging/components/SliderInput';
import InputLabel from 'evcharging/components/InputLabel';
import { Theme } from 'evcharging/app/themes';
import { css } from '@emotion/css';
import useTheme from 'evcharging/hooks/useTheme';
import Title from 'evcharging/components/Title';
import Spacer from 'evcharging/components/Spacer';
import Description from 'evcharging/components/Description';
import Input from 'evcharging/components/Input';
import Switcher from 'evcharging/components/Switcher';
import Mobile from 'evcharging/components/Mobile';
import Link from 'evcharging/components/Link';
import { EV_CALCULATOR_URL, EV_REBATES_URL } from 'evcharging/app/seo/urls';
import { useRouter } from 'next/router';
import useI18n from 'evcharging/hooks/useI18n';

const calcEstimatedCost = (
  chargingPorts: number,
  charger: number,
  daysPerMonth: number,
  hoursPerDay: number,
  electricRate: number
) => chargingPorts * charger * daysPerMonth * hoursPerDay * electricRate;

const calcTotalMonthlyCost = (estimatedCost: number, chargingPorts: number, fee: number) =>
  estimatedCost + fee * chargingPorts;

const calcSoldByKWh = (
  chargingPorts: number,
  charger: number,
  daysPerMonth: number,
  hoursPerDay: number,
  chargePerKwh: number
) => chargingPorts * charger * daysPerMonth * hoursPerDay * chargePerKwh;

const calcSoldByHour = (
  chargingPorts: number,
  daysPerMonth: number,
  hoursPerDay: number,
  chargePerHour: number
) => chargingPorts * daysPerMonth * hoursPerDay * chargePerHour;

enum SellType {
  KWH = 'kwh',
  HOUR = 'hour',
  MIXED = 'mixed',
}

const Calculator = () => {
  const { push, query } = useRouter();
  const { t } = useI18n();

  const theme = useTheme();
  const styles = createStyles(theme);
  const [sellType, setSellType] = useState<string>(
    query.sellType && ['kwh', 'hour', 'mixed'].includes(String(query.sellType))
      ? String(query.sellType)
      : SellType.KWH
  );
  const chargingPorts = useInput([numbersOnly, min(1), max(10000)], query.chargingPorts ?? '0');
  const electricRate = useInput([], query.electricRate ?? '0');
  const chargePerKwh = useInput([], query.chargePerKwh ?? '0');
  const chargePerHour = useInput([], query.chargePerHour ?? '0');
  const [hoursPerDay, setHoursPerDay] = useState<number>(
    query.hoursPerDay ? Number(query.hoursPerDay) : 0
  );
  const [daysPerMonth, setDaysPerMonth] = useState<number>(
    query.daysPerMonth ? Number(query.daysPerMonth) : 0
  );
  const idleFee = useInput([], query.idleFee ?? '0');

  const fee = 30;
  const charger = 7.6;
  const days = Array.from({ length: daysPerMonth }, (_, i) => i + 1);

  const expenses = days.map((day) => {
    const estimatedCost = calcEstimatedCost(
      Number(chargingPorts.value),
      Number(charger),
      day,
      hoursPerDay,
      Number(electricRate.value)
    );

    return calcTotalMonthlyCost(estimatedCost, Number(chargingPorts.value), fee);
  });

  const revenue = days.map((day) => {
    const byKWh = calcSoldByKWh(
      Number(chargingPorts.value),
      Number(charger),
      day,
      hoursPerDay,
      Number(chargePerKwh.value)
    );

    const byHour = calcSoldByHour(
      Number(chargingPorts.value),
      day,
      hoursPerDay,
      Number(chargePerHour.value)
    );

    switch (sellType) {
      case SellType.KWH:
        return byKWh;
      case SellType.HOUR:
        return byHour;
      default:
        return byHour + byKWh;
    }
  });

  const profit = revenue.map((rev, index) => rev - expenses[index]);

  const estimatedCost = calcEstimatedCost(
    Number(chargingPorts.value),
    Number(charger),
    daysPerMonth,
    hoursPerDay,
    Number(electricRate.value)
  );

  const totalMonthlyCost = calcTotalMonthlyCost(estimatedCost, Number(chargingPorts.value), fee);

  const soldByKWh = calcSoldByKWh(
    Number(chargingPorts.value),
    Number(charger),
    daysPerMonth,
    hoursPerDay,
    Number(chargePerKwh.value)
  );

  const soldByHour = calcSoldByHour(
    Number(chargingPorts.value),
    daysPerMonth,
    hoursPerDay,
    Number(chargePerHour.value)
  );

  const monthlyRevenue =
    (sellType === SellType.KWH
      ? soldByKWh
      : sellType === SellType.HOUR
      ? soldByHour
      : soldByKWh + soldByHour) + Number(idleFee.value);

  const paymentFee = (5 * monthlyRevenue) / 100; // 5%
  const netProfit = monthlyRevenue - totalMonthlyCost - paymentFee;

  const shallowPush = (params: Record<string, any>) => {
    push(
      `${EV_CALCULATOR_URL}?${new URLSearchParams({
        ...query,
        ...params,
      }).toString()}`,
      undefined,
      {
        shallow: true,
      }
    ).then();
  };

  const onHoursPerDayClick = (value: number) => {
    setHoursPerDay(value);
    shallowPush({ hoursPerDay: value });
  };

  const onDaysPerMonthClick = (value: number) => {
    setDaysPerMonth(value);
    shallowPush({ daysPerMonth: value });
  };

  return (
    <Responsible fractions={[50, 48]}>
      <Box>
        <Title variant={'h2'}>{t('calculator.title')}</Title>
        <Spacer value={15} mobile={6} />
        <Description>
          {t('calculator.info1')}{' '}
          <Link href={EV_REBATES_URL}>
            <u>{t('calculator.info2')}</u>
          </Link>{' '}
          {t('calculator.info3')}
        </Description>
        <Spacer value={50} mobile={24} />
        <Mobile>
          <hr style={{ border: '1px solid #EBEBEB' }} />
          <Spacer value={24} />
        </Mobile>
        <Input
          required
          label={t('calculator.form.ports')}
          placeholder={t('calculator.form.type')}
          value={chargingPorts.value}
          onChangeValue={(value) => {
            chargingPorts.onChangeValue(value);
            shallowPush({ chargingPorts: value });
          }}
        />
        <Input
          required
          label={t('calculator.form.rate')}
          placeholder={t('calculator.form.type')}
          tooltip={t('calculator.form.local')}
          value={electricRate.value}
          type="number"
          onChangeValue={(value) => {
            electricRate.onChangeValue(value);
            shallowPush({ electricRate: value });
          }}
        />

        <Switcher
          value={sellType}
          items={[
            { value: SellType.KWH, title: t('calculator.form.perkwh') },
            { value: SellType.HOUR, title: t('calculator.form.perhour') },
            { value: SellType.MIXED, title: t('calculator.form.mixed') },
          ]}
          onChange={(value) => {
            setSellType(value);
            shallowPush({ sellType: value });
          }}
        />
        {sellType === SellType.KWH && (
          <Input
            required
            label={t('calculator.form.perkwh')}
            placeholder={t('calculator.form.type')}
            tooltip={t('calculator.form.hintkwh')}
            value={chargePerKwh.value}
            type="number"
            onChangeValue={(value) => {
              chargePerKwh.onChangeValue(value);
              shallowPush({ chargePerKwh: value });
            }}
          />
        )}
        {sellType === SellType.HOUR && (
          <Input
            required
            label={t('calculator.form.perhour')}
            placeholder={t('calculator.form.type')}
            tooltip={t('calculator.form.hinthour')}
            value={chargePerHour.value}
            type="number"
            onChangeValue={(value) => {
              chargePerHour.onChangeValue(value);
              shallowPush({ chargePerHour: value });
            }}
          />
        )}

        {sellType === SellType.MIXED && (
          <Responsible fractions={[48.3, 48.3]} space={0}>
            <Input
              required
              label={t('calculator.form.perkwh')}
              placeholder={t('calculator.form.type')}
              tooltip={t('calculator.form.hintkwh')}
              value={chargePerKwh.value}
              type="number"
              onChangeValue={(value) => {
                chargePerKwh.onChangeValue(value);
                shallowPush({ chargePerKwh: value });
              }}
            />
            <Input
              required
              label={t('calculator.form.perhour')}
              placeholder={t('calculator.form.type')}
              tooltip={t('calculator.form.hinthour')}
              value={chargePerHour.value}
              type="number"
              onChangeValue={(value) => {
                chargePerHour.onChangeValue(value);
                shallowPush({ chargePerHour: value });
              }}
            />
          </Responsible>
        )}

        <InputLabel
          required
          label={t('calculator.form.hours')}
          tooltip={t('calculator.form.hinthours')}
        />
        <SliderInput onChange={onHoursPerDayClick} value={hoursPerDay} min={1} max={24} />

        <InputLabel
          required
          label={t('calculator.form.days')}
          tooltip={t('calculator.form.hintdays')}
        />
        <SliderInput onChange={onDaysPerMonthClick} value={daysPerMonth} min={1} max={31} />
        <Input
          label={'Idling Fees $/hour'}
          placeholder={t('calculator.form.type')}
          tooltip={'Fees for idling once the vehicle is done charging'}
          value={idleFee.value}
          type="number"
          onChangeValue={(value) => {
            idleFee.onChangeValue(value);
            shallowPush({ idleFee: value });
          }}
        />
      </Box>
      <div className={styles.result}>
        <CalculatorChart days={days} expenses={expenses} revenue={revenue} profit={profit} />
        <CalculatorResults
          fee={fee}
          paymentFee={paymentFee}
          chargingPorts={Number(chargingPorts.value)}
          estimatedCost={estimatedCost}
          totalMonthlyCost={totalMonthlyCost}
          monthlyRevenue={monthlyRevenue}
          netProfit={netProfit}
          electricRate={Number(electricRate.value)}
          chargePerHour={Number(chargePerHour.value)}
          chargePerKwh={Number(chargePerKwh.value)}
          daysPerMonth={daysPerMonth}
          hoursPerDay={hoursPerDay}
          sellType={sellType}
        />
      </div>
    </Responsible>
  );
};

const createStyles = (theme: Theme) => ({
  result: css`
    display: flex;
    flex-direction: column;
    border-left: 1px solid #ebebeb;
    padding-left: 27px;
    ${theme.breakpoints.mobile.down} {
      border-left: none;
      padding-left: 0;
    }
  `,
});

export default Calculator;
